import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { ConfigProvider, DatePicker, GlobalToken } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

import { AntdLanguageMapping } from '@/i18n/i18n';
import {
  getStartLimiterFunc,
  getEndLimiterFunc,
} from '@/comp/InvestigationDashboard/TabPane/ActionBar/ActionBar.utils';

const { RangePicker } = DatePicker;

interface EPRangePickerProps {
  value: [Dayjs, Dayjs] | null;
  onOpenHideCurrentValue?: boolean;
  useRangeLimiters?: boolean;
  disableFutureDates?: boolean;
  allowEmpty?: [boolean, boolean];
  allowClear?: boolean;
  disabled?: [boolean, boolean];
  className?: string;
  startDateDisabledDate?: (date: Dayjs) => boolean;
  onChange?: (dates: [Dayjs, Dayjs] | null) => void;
  onCalendarChange?: (dates: [Dayjs, Dayjs] | null) => void;
}

const EPRangePicker = ({
  onCalendarChange,
  useRangeLimiters,
  startDateDisabledDate,
  disableFutureDates,
  onOpenHideCurrentValue,
  value,
  ...rest
}: EPRangePickerProps) => {
  // Fixed date disabling function
  const disabledFutureDateFunction = useCallback(
    (current: Dayjs) => {
      return Boolean(current && disableFutureDates && current.isAfter(dayjs().endOf('day')));
    },
    [disableFutureDates],
  );

  const [dates, setDates] = useState<[Dayjs, Dayjs] | null>(null);
  const [changeStartedOnStartDate, setChangeStartedOnStartDate] = useState<boolean | null>(null);
  const [disabledDates, setDisabledDates] = useState<(date: Dayjs) => boolean>(
    useRangeLimiters ? disabledFutureDateFunction : startDateDisabledDate || (() => false),
  );
  const [hackValue, setHackValue] = useState<[Dayjs | null, Dayjs | null] | undefined>();

  useEffect(() => {
    if (!useRangeLimiters) {
      setDisabledDates(() => startDateDisabledDate || (() => false));
    }
  }, [useRangeLimiters, startDateDisabledDate]);

  const onCalendarChangeHandler = useCallback(
    (newDates: [Dayjs, Dayjs] | null, _dateStrings: string[], info: { range: 'start' | 'end' }) => {
      // Handle possible invalid date situations
      if (newDates && newDates[0] && newDates[1] && newDates[0].isAfter(newDates[1])) {
        return;
      }

      setDates(newDates);
      onCalendarChange && onCalendarChange(newDates);

      if (useRangeLimiters) {
        const isStartRange = info.range === 'start';
        const startedOnStart = changeStartedOnStartDate ?? isStartRange;
        const isFirstStep = changeStartedOnStartDate === null;

        if (isFirstStep) {
          setChangeStartedOnStartDate(isStartRange);
        }

        const pickedStart = newDates?.[0] || null;
        const pickedEnd = newDates?.[1] || null;

        if (startedOnStart) {
          // User picked start date first -> limit end date
          setDisabledDates(() => date => getEndLimiterFunc([pickedStart, null])(date));
        } else {
          // User picked end date first -> limit start date
          setDisabledDates(() => date => getStartLimiterFunc([null, pickedEnd])(date));
        }
      }
    },
    [changeStartedOnStartDate, disabledFutureDateFunction, onCalendarChange, startDateDisabledDate, useRangeLimiters],
  );

  const onOpenChange = (open: boolean) => {
    if (useRangeLimiters) {
      // Reset selection state when calendar opens/closes
      setChangeStartedOnStartDate(null);
      setDisabledDates(() => disabledFutureDateFunction);
    } else {
      setDisabledDates(() => startDateDisabledDate);
    }

    // Fixed empty value handling - don't use array cast
    if (onOpenHideCurrentValue && !useRangeLimiters) {
      if (open) {
        setHackValue([value?.[0] ?? null, null]);
      } else {
        setHackValue(undefined);
      }
    }
  };

  return (
    <ConfigProvider locale={AntdLanguageMapping[localStorage.getItem('language') || 'en']}>
      <RangePicker
        size="large"
        value={hackValue || value}
        renderExtraFooter={() => <ExtraFooter dates={dates} />}
        suffixIcon={null}
        format={'ddd, D MMM YYYY'}
        separator={'-'}
        onCalendarChange={onCalendarChangeHandler}
        disabledDate={disabledDates}
        onOpenChange={onOpenChange}
        picker="date"
        defaultPickerValue={[dayjs(), dayjs()]}
        {...rest}
      />
    </ConfigProvider>
  );
};

interface ExtraFooterProps {
  dates: [Dayjs, Dayjs] | null;
}

const ExtraFooter = ({ dates }: ExtraFooterProps) => {
  const [startDate, endDate] = dates || [];
  const format = 'ddd, D MMM YYYY';
  const startDateFormatted = startDate?.format(format);
  const endDateFormatted = endDate?.format(format);

  return (
    <ExtraFooterContainer>
      {startDateFormatted && <span>{startDateFormatted} - </span>}
      {endDateFormatted && <span>{endDateFormatted}</span>}
    </ExtraFooterContainer>
  );
};

const ExtraFooterContainer = styled.div<{ theme: { token: GlobalToken } }>`
  min-height: 44px;
  text-align: center;
  font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
  padding: ${({ theme: { token } }) => token.paddingXXS}px 0;
`;

export default EPRangePicker;